import { AutoComplete, Button, Col, Drawer, Form, Input, Row, Select } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { Formik } from 'formik';
import { FiCheck } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';

import {
  ORGANIZATION_UNIT_LISTS_FORM,
  ORGANIZATION_UNIT_LISTS_FORM_SCHEMA,
  ORGANIZATION_UNIT_LISTS_INITIAL_FORM_VALUES,
} from '../../OrganizationUnitLists.constants.tsx';
import { searchLeadership } from '../../OrganizationUnitLists.services.ts';
import type { OrganizationUnitListDrawerProps } from '../../OrganizationUnitLists.types.ts';

import './OrganizationUnitListsDrawer.styles.css';
import { DebounceSelect } from '../../../../components/debonce-select';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { getOrganizationUnitListValues } from '../../OrganizationUnitLists.helpers.ts';
import { useService } from '../../OrganizationUnitLists.hooks.ts';

export default function OrganizationUnitListsDrawer({
  onClose,
  open,
  onSubmit,
  organizationUnitListId,
}: OrganizationUnitListDrawerProps) {
  const { t } = useTranslation();
  const { unit, units } = useService({ organizationUnitListId });

  const isNonEmptyString = (x: unknown): x is string =>
    typeof x === 'string' && x.trim().length > 0;

  const parentAbbreviations: DefaultOptionType[] = Array.from(
    new Set<string>(
      (units ?? [])
        .map((u: { abbreviation: string }) => u?.abbreviation as unknown)
        .filter(isNonEmptyString),
    ),
  ).map((v) => ({ value: v, label: v }));
  const initialValues = organizationUnitListId
    ? getOrganizationUnitListValues(unit)
    : ORGANIZATION_UNIT_LISTS_INITIAL_FORM_VALUES;
  const validOptions = units?.map((u: { abbreviation: string }) => u?.abbreviation as string) || [];

  return (
    <Drawer
      title={organizationUnitListId ? unit?.title : t('pages.organization-unit-lists.create')}
      placement="right"
      closable={false}
      width={500}
      onClose={onClose}
      open={open}
      extra={
        <Row justify="end" align="middle" gutter={8}>
          <span onClick={onClose} style={{ cursor: 'pointer' }}>
            <MdClose fontSize={20} />
          </span>
        </Row>
      }
      footer={
        <Row justify="end" gutter={8}>
          <Col>
            <Button size="large" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </Col>
          <Col>
            <Button
              size="large"
              type="primary"
              icon={<FiCheck />}
              iconPosition="end"
              htmlType="submit"
              form={ORGANIZATION_UNIT_LISTS_FORM}
            >
              {t('common.confirm')}
            </Button>
          </Col>
        </Row>
      }
    >
      <Formik
        initialValues={initialValues}
        validationSchema={Yup.object().shape({
          ...ORGANIZATION_UNIT_LISTS_FORM_SCHEMA,
          parentAbbreviation: Yup.string()
            .trim()
            .oneOf(validOptions, t('error.validation.valid-abbreviation')),
        })}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => {
          return (
            <Form layout="vertical" onFinish={handleSubmit} id={ORGANIZATION_UNIT_LISTS_FORM}>
              <Form.Item
                label={t('form.field.name')}
                validateStatus={touched.title && errors.title ? 'error' : ''}
                help={touched.title && typeof errors.title === 'string' ? errors.title : undefined}
              >
                <Input
                  name="title"
                  variant="underlined"
                  value={values.title}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.order')}
                validateStatus={touched.order && errors.order ? 'error' : ''}
                help={touched.order && typeof errors.order === 'string' ? errors.order : undefined}
              >
                <Input
                  name="order"
                  type="number"
                  variant="underlined"
                  value={values.order}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.type')}
                validateStatus={touched.type && errors.type ? 'error' : ''}
                help={touched.type && typeof errors.type === 'string' ? errors.type : undefined}
              >
                <Select
                  variant="underlined"
                  value={values.type}
                  onChange={(e) => setFieldValue('type', e)}
                  options={[
                    { value: 1, label: t('form.option.regions') },
                    { value: 2, label: t('form.option.headquarters') },
                  ]}
                  placeholder={t('form.placeholder.select-here')}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.abbreviation')}
                validateStatus={touched.abbreviation && errors.abbreviation ? 'error' : ''}
                help={
                  touched.abbreviation && typeof errors.abbreviation === 'string'
                    ? errors.abbreviation
                    : undefined
                }
              >
                <Input
                  name="abbreviation"
                  variant="underlined"
                  value={values.abbreviation}
                  onChange={handleChange}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.parent-abbreviation')}
                validateStatus={
                  touched.parentAbbreviation && errors.parentAbbreviation ? 'error' : ''
                }
                help={
                  touched.parentAbbreviation && typeof errors.parentAbbreviation === 'string'
                    ? errors.parentAbbreviation
                    : undefined
                }
              >
                {/*<Input*/}
                {/*  name="parentAbbreviation"*/}
                {/*  variant="underlined"*/}
                {/*  value={values.parentAbbreviation}*/}
                {/*  onChange={handleChange}*/}
                {/*  placeholder={t('form.placeholder.type-here')}*/}
                {/*/>*/}
                <AutoComplete
                  variant="underlined"
                  value={values.parentAbbreviation}
                  options={parentAbbreviations}
                  onSelect={(val) => setFieldValue('parentAbbreviation', val)}
                  onSearch={(val) => setFieldValue('parentAbbreviation', val)}
                  filterOption={(input, option) => {
                    const q = (input ?? '').toLowerCase();
                    const v = (option?.value ?? '').toString().toLowerCase();
                    const l = typeof option?.label === 'string' ? option.label.toLowerCase() : '';
                    return v.includes(q) || l.includes(q);
                  }}
                  placeholder={t('form.placeholder.type-here')}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.leadership')}
                validateStatus={touched.leadership && errors.leadership ? 'error' : ''}
                help={
                  touched.leadership && typeof errors.leadership === 'string'
                    ? errors.leadership
                    : undefined
                }
              >
                <DebounceSelect
                  mode="multiple"
                  variant="underlined"
                  value={values.leadership}
                  placeholder={t('form.placeholder.select-here')}
                  fetchOptions={(term) => searchLeadership(term)}
                  style={{ width: '100%' }}
                  onChange={(newValue) => {
                    if (Array.isArray(newValue)) {
                      setFieldValue('leadership', newValue);
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                label={t('form.field.selectable')}
                validateStatus={touched.selectable && errors.selectable ? 'error' : ''}
                help={
                  touched.selectable && typeof errors.selectable === 'string'
                    ? errors.selectable
                    : undefined
                }
              >
                <Select
                  variant="underlined"
                  value={values.selectable}
                  placeholder={t('form.placeholder.select-here')}
                  onChange={(e) => setFieldValue('selectable', e)}
                  options={[
                    { value: true, label: t('common.yes') },
                    { value: false, label: t('common.no') },
                  ]}
                />
              </Form.Item>
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
}
